/**
 * Live price search
 *
 * Asks the worker's /price route for a current market price for one input.
 * The worker searches approved price reporters, reads the page, and decides in
 * code whether the number is `sourced` (see workers/gemini-proxy/src/price).
 * Only a sourced price may set an input's cost; anything else is shown to the
 * user with the reasons it failed, and the user decides.
 */

import { CONFIDENCE } from './costProvenance';

const WORKER_URL = process.env.REACT_APP_GEMINI_WORKER_URL || '';

/**
 * @param {object} req  { material, spec?, unit, context? }
 * @returns {Promise<object>} the worker's result: status 'sourced' | 'unverified' | 'not_found'
 */
export async function searchPrice(req, { baseUrl = WORKER_URL, fetchImpl = fetch, timeoutMs = 150000 } = {}) {
  if (!baseUrl) throw new Error('Live price search needs the LiTeCAB worker (REACT_APP_GEMINI_WORKER_URL is not set).');
  const resp = await fetchImpl(`${baseUrl.replace(/\/$/, '')}/price`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(req),
    signal: typeof AbortSignal !== 'undefined' && AbortSignal.timeout ? AbortSignal.timeout(timeoutMs) : undefined,
  });
  let body = null;
  try {
    body = await resp.json();
  } catch {
    // Reported below with the status code.
  }
  if (!resp.ok) throw new Error(body?.error || `price search failed (${resp.status})`);
  return body;
}

/** The fields a sourced result writes onto an input, or null when it may not be applied. */
export function sourcedCost(result) {
  if (result?.status !== 'sourced' || !Number.isFinite(result.price) || result.price <= 0) return null;
  const s = result.source || {};
  const date = s.date || '';
  return {
    cost_per_unit: result.price,
    cost_unit: `USD/${result.unit}`,
    cost_source: date ? `${s.name || s.domain} (${date})` : (s.name || s.domain || ''),
    cost_year: (date.match(/\d{4}/) || [''])[0],
    cost_confidence: CONFIDENCE.SOURCED,
    cost_url: s.url || '',
    cost_quote: s.quote || '',
    cost_date: date,
    cost_note: result.note || '',
  };
}

/** What the copilot needs to report a result, without the worker's debug detail. */
export function describePriceResult(result) {
  const s = result?.source || {};
  return {
    status: result?.status,
    price_usd: result?.price ?? null,
    unit: result?.unit,
    source: s.url ? { name: s.name, url: s.url, date: s.date, quote: s.quote, grade: s.grade } : null,
    printed: result?.printed || null,
    reasons: result?.reasons?.length ? result.reasons : undefined,
    note: result?.note || undefined,
  };
}
